/**
 * Activity stream -> what the two panes actually render.
 *
 * The projector already dropped events that should never have been emitted.
 * Everything arriving here is true; the problem is shape. An agent writing
 * eight files produces eight true events, and rendering them as eight rows is
 * what turned the build view into a wall of near-identical text.
 *
 * Two collapses happen here:
 *
 *   Steps — a run-length fold. Repeating the same thing increments a count
 *   rather than adding a row, and a heartbeat advances the active step's timer
 *   rather than appending. A quiet two-minute install becomes one row that
 *   ages, which reads as one thing taking a while instead of eight things
 *   happening.
 *
 *   Files — a map keyed by path. Repetition is structurally impossible: twenty
 *   writes to one file is one entry with a touch count.
 *
 * This module is pure and deterministic over an activity array, deliberately
 * separate from the reducer so it can be tested without an EventSource.
 */

import type { Activity, BuildPhase, TerminalSessionState } from "@hatch/shared";

/** How many paths a burst step exposes before it stops naming them. */
const MAX_BURST_PATHS = 4;

/**
 * How many output lines a single step retains.
 *
 * A build can emit tens of thousands of lines; the oldest of them stopped being
 * interesting the moment the next command ran. Keeping the tail bounds both the
 * DOM and the memory the view model holds.
 */
const MAX_STEP_OUTPUT = 200;

export type StepKind = "narration" | "artifact" | "phase" | "terminal";

export interface OutputLine {
  seq: number;
  stream: "stdout" | "stderr";
  line: string;
}

/**
 * A step's command and what it printed.
 *
 * These are the same log activities the output drawer used to pool at the
 * bottom of the pane. Pooled, they were unattributable — you could see that
 * something printed 3,189 lines but not which of forty steps printed them.
 * Attached to the step, the command and its output read as one thing.
 */
export interface StepIo {
  /** The command that opened this step, when there was one. */
  input?: string;
  /** Trailing output, oldest first. */
  output: OutputLine[];
}

export interface Step {
  /** Stable within a render: the seq of the activity that opened the step. */
  id: number;
  kind: StepKind;
  label: string;
  /**
   * How many times this same thing happened consecutively. For an artifact
   * burst this counts SUCCESSFUL writes: a `failed` correction decrements it
   * and increments `failedCount` instead, so the label never reports a write
   * the tool answered with an error.
   */
  count: number;
  /** Writes in this burst whose observation came back an error. */
  failedCount?: number;
  /** Epoch ms of the activity that opened the step. */
  at: number;
  /** Set by heartbeats while this step is the active one. */
  elapsedMs?: number;
  /** Recent short paths, for an artifact burst's detail line. */
  paths?: string[];
  /** Present on a terminal step. */
  outcome?: TerminalSessionState;
  /** The command and output this step produced, when it produced any. */
  io?: StepIo;
}

export interface FileEntry {
  path: string;
  /** Trailing segments, which is what the structure strip renders. */
  short: string;
  /** Directory the file sits in, used to group the strip. */
  dir: string;
  action: "created" | "modified" | "deleted" | "failed";
  /**
   * Successful writes to this path, netted the same way `Step.count` is: a
   * `failed` artifact corrects the write counted at call time back out. A file
   * at zero was never actually produced and stays out of the file count.
   */
  touches: number;
}

export type CanvasState =
  | { state: "nest" }
  | { state: "framing"; siteUrl: string }
  | { state: "closed"; outcome: TerminalSessionState; siteUrl?: string; message?: string };

export interface ViewModel {
  steps: Step[];
  files: FileEntry[];
  /** Unique files touched — not the number of writes. */
  fileCount: number;
  currentAction: string;
  canvas: CanvasState;
}

/** Trailing path segments, matching how the API shortens container paths. */
export function shortPath(path: string): string {
  return path.split("/").filter(Boolean).slice(-2).join("/");
}

/** Past-tense phrasing for a file action. */
export function describeArtifactAction(path: string, action: string): string {
  // Before this branch, "failed" fell through to the default verb and the
  // current-action line read "Created xano/index.ts" for a write that just
  // errored — the exact dishonesty the failed action exists to remove.
  if (action === "failed") return `Write failed: ${shortPath(path)}`;
  const verb = action === "deleted" ? "Removed" : action === "modified" ? "Updated" : "Created";
  return `${verb} ${shortPath(path)}`;
}

/**
 * Label for an artifact step, which changes shape once it is a burst.
 *
 * `written` and `failed` are the step's netted counts. The all-failed shapes
 * lead with the failure because there is no success to report; the mixed shape
 * reports both so "4 files written" can never stand over four error lines —
 * the exact mis-render that motivated the failed action.
 */
function burstLabel(written: number, failed: number, latestPath: string, action: string): string {
  if (failed === 0) {
    return written === 1 ? describeArtifactAction(latestPath, action) : `${written} files written`;
  }
  if (written === 0) {
    return failed === 1
      ? describeArtifactAction(latestPath, "failed")
      : `${failed} writes failed`;
  }
  return `${written} file${written === 1 ? "" : "s"} written, ${failed} failed`;
}

export function buildViewModel(activities: Activity[]): ViewModel {
  const steps: Step[] = [];
  const files = new Map<string, FileEntry>();

  let canvas: CanvasState = { state: "nest" };
  let currentAction: string | null = null;

  /**
   * Output that arrived with no step to hang it on — before the first step, or
   * straight after a phase divider, which is a rule and not something that
   * prints. Held rather than dropped, and flushed into the next real step.
   */
  let pending: Activity[] = [];

  /** The step a heartbeat would update, and that a repeat would coalesce into. */
  const active = () => steps.at(-1);

  /** The step output attaches to. A phase divider is not one. */
  const owner = () => {
    const step = active();
    return step && step.kind !== "phase" ? step : undefined;
  };

  function attach(step: Step, log: Extract<Activity, { kind: "log" }>) {
    const io = (step.io ??= { output: [] });
    // The command echo, which the projector marks. Only the first one, and only
    // before anything has printed — a later marked line is output that happens
    // to be a command, not this step's own invocation.
    if (log.role === "input" && io.input === undefined && io.output.length === 0) {
      io.input = log.line;
      return;
    }
    io.output.push({ seq: log.seq, stream: log.stream, line: log.line });
    if (io.output.length > MAX_STEP_OUTPUT) {
      io.output.splice(0, io.output.length - MAX_STEP_OUTPUT);
    }
  }

  function open(step: Step) {
    steps.push(step);
    if (pending.length > 0 && step.kind !== "phase") {
      for (const held of pending) attach(step, held as Extract<Activity, { kind: "log" }>);
      pending = [];
    }
  }

  for (const activity of activities) {
    switch (activity.kind) {
      case "log": {
        const step = owner();
        if (step) attach(step, activity);
        else pending = [...pending, activity].slice(-MAX_STEP_OUTPUT);
        break;
      }

      case "narration": {
        currentAction = activity.message;
        const step = active();
        if (step?.kind === "narration" && step.label === activity.message) {
          // Same thing again: count it rather than repeat it. Run-length, not
          // global dedup — a genuine return to earlier work is information.
          step.count += 1;
          // A second invocation ran, so the block below the row belongs to it.
          // Keeping the first would show one command's output under another's,
          // and appending would file the new command as the old one's output.
          delete step.io;
          break;
        }
        open({
          id: activity.seq,
          kind: "narration",
          label: activity.message,
          count: 1,
          at: activity.at,
        });
        break;
      }

      case "artifact": {
        currentAction = describeArtifactAction(activity.path, activity.action);
        // A `failed` artifact is the relay's correction for a write already
        // counted at call time — it marks that write failed rather than
        // recording a new one.
        const failedWrite = activity.action === "failed";

        const existing = files.get(activity.path);
        if (existing) {
          if (failedWrite) {
            existing.touches = Math.max(0, existing.touches - 1);
            // A failed RETRY of a file that already landed does not unmake the
            // file: its content from the earlier successful write is still on
            // disk, so the entry keeps its real action until nothing survives.
            if (existing.touches === 0) existing.action = "failed";
          } else {
            existing.touches += 1;
            existing.action = activity.action;
          }
        } else {
          const segments = activity.path.split("/").filter(Boolean);
          files.set(activity.path, {
            path: activity.path,
            short: shortPath(activity.path),
            dir: segments.slice(-2, -1)[0] ?? "",
            action: activity.action,
            touches: failedWrite ? 0 : 1,
          });
        }

        const step = active();
        if (step?.kind === "artifact") {
          if (failedWrite) {
            // The corrected write is in THIS burst in the ordinary flow — the
            // observation follows its call directly. If narration split the
            // burst in between, the correction lands on the active artifact
            // step rather than hunting backwards: the netted counts stay
            // right, which is what the ship gate and the label read.
            step.count = Math.max(0, step.count - 1);
            step.failedCount = (step.failedCount ?? 0) + 1;
          } else {
            step.count += 1;
            step.paths = [...(step.paths ?? []), shortPath(activity.path)].slice(-MAX_BURST_PATHS);
          }
          step.label = burstLabel(step.count, step.failedCount ?? 0, activity.path, activity.action);
          break;
        }
        open({
          id: activity.seq,
          kind: "artifact",
          label: describeArtifactAction(activity.path, activity.action),
          count: failedWrite ? 0 : 1,
          failedCount: failedWrite ? 1 : 0,
          at: activity.at,
          paths: [shortPath(activity.path)],
        });
        break;
      }

      case "heartbeat": {
        // Never opens a step. A heartbeat is not progress — it is the same
        // thing still happening — so it advances the active step's timer and
        // nothing else. Appending here is what produced the columns of
        // "Running view · 20s / · 41s / · 1m 4s".
        const step = active();
        if (step) step.elapsedMs = activity.elapsedInPhaseMs;
        if (currentAction === null) currentAction = activity.lastAction;
        break;
      }

      case "phase":
        // Closes the current run so identical narration either side of a phase
        // change stays separate — crossing that boundary is real information.
        open({
          id: activity.seq,
          kind: "phase",
          label: activity.phase,
          count: 1,
          at: activity.at,
        });
        break;

      case "terminal": {
        open({
          id: activity.seq,
          kind: "terminal",
          label: activity.message ?? (activity.outcome === "succeeded" ? "Build finished" : "Build stopped"),
          count: 1,
          at: activity.at,
          outcome: activity.outcome,
        });
        canvas =
          activity.outcome === "succeeded" && activity.siteUrl
            ? { state: "framing", siteUrl: activity.siteUrl }
            : {
                state: "closed",
                outcome: activity.outcome,
                ...(activity.siteUrl ? { siteUrl: activity.siteUrl } : {}),
                ...(activity.message ? { message: activity.message } : {}),
              };
        break;
      }
    }
  }

  const fileList = [...files.values()];

  return {
    steps,
    files: fileList,
    // A file whose every write failed was never actually produced; counting it
    // would put the failed-writes dishonesty one pane over, in the structure
    // strip's total. A file with at least one successful write counts once.
    fileCount: fileList.filter((f) => f.touches > 0).length,
    currentAction: currentAction ?? "Getting started",
    canvas,
  };
}
